// Admin-only panel for editing intervention copy.
//
// Loads default tasks + theme from the bundled frontend modules, merges any
// existing admin overrides from /api/admin/content/:type, and lets the
// admin tweak any field. On save, computes the diff vs defaults and PUTs
// only the changed fields. Saving is publish-immediately; a confirmation
// modal stands between the save click and going live for all users.
//
// Notes:
//   - Tasks 1, 2 and 9 are theme-independent (they live in interventionTasks).
//     Tasks 3-8 are *also* in interventionTasks but the actual UI copy comes
//     from interventionGraphs.js + interventionThemes.js. To keep this
//     editor focused on copy that matters, we surface:
//       * Tasks 1..9 (title + content + inputPrompt) — these change tasks 1,2,9.
//       * Theme fields under t3..t8 — these change the rendered copy for
//         tasks 3..8 via the template builder.
//     Editing both surfaces gives the admin full control.

import { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { Save, RotateCcw, AlertTriangle, ChevronDown, FileText, Sparkles } from "lucide-react";
import { Button } from "./ui/button";
import { Textarea } from "./ui/textarea";
import { Input } from "./ui/input";
import { Dialog, DialogContent, DialogTitle, DialogDescription, DialogFooter } from "./ui/dialog";
import { toast } from "sonner";
import INTERVENTION_TASKS from "../lib/interventionTasks";
import THEMES from "../lib/interventionThemes";
import { setContentOverrideFor, loadContentOverrides } from "../lib/contentOverrides";
import logger from "../lib/logger";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Friendly labels for theme fields. Keys mirror interventionThemes.js exactly.
const THEME_FIELD_LABELS = {
  theme: "Theme noun (used in shared sentences, e.g. 'the worry')",
  t3: {
    _label: "Task 3 — Calming the Body",
    opener: "Opener (encouragement)",
    bodyResponse: "Body response examples",
    systemNote: "System note (one-liner explaining the body response)",
    emotionalDetail: "Emotional detail examples",
    bodyTension: "Body tension list",
    emotionalList: "Emotional list",
  },
  t4: {
    _label: "Task 4 — Facing the Story",
    subject: "Story subject (e.g. 'the worry itself')",
    storyIntro: "Story intro paragraph",
    storyNoun: "Story noun (e.g. 'the worry story')",
  },
  t5: {
    _label: "Task 5 — Second Pass",
    gripPhrase: "Grip phrase (e.g. 'loosen more of the grip worry has on you')",
    storyReplay: "Story replay instruction",
    followup: "Follow-up line",
  },
  t6: {
    _label: "Task 6 — Imagining the Positive",
    contextQuestion: "Context question",
    positiveVision: "Positive vision paragraph",
  },
  t7: {
    _label: "Task 7 — Replacing the Old Story",
    transformations: "Transformations sentence",
    metaphor: "Metaphor (e.g. 'just like replacing old stained carpet...')",
  },
  t8: {
    _label: "Task 8 — Future-Proofing",
    triggerExamples: "Trigger examples",
  },
};

const isLongField = (value) =>
  typeof value === "string" && (value.length > 90 || value.includes("\n"));

function diffTasks(defaults, edited) {
  // Returns the list of task patches that differ from defaults.
  const patches = [];
  defaults.forEach((task, idx) => {
    const e = edited[idx] || {};
    const patch = { index: idx };
    let changed = false;
    ["title", "content", "inputPrompt"].forEach((key) => {
      const base = task[key] ?? "";
      const next = e[key] ?? "";
      if (String(base) !== String(next)) {
        patch[key] = next;
        changed = true;
      }
    });
    if (changed) patches.push(patch);
  });
  return patches;
}

function diffTheme(defaults, edited) {
  // Recursive diff. Returns a nested object containing only the fields
  // that differ from defaults.
  if (defaults == null) defaults = {};
  if (edited == null) edited = {};
  const result = {};
  for (const key of Object.keys(edited)) {
    const base = defaults[key];
    const next = edited[key];
    if (typeof next === "object" && next !== null && !Array.isArray(next)) {
      const sub = diffTheme(base || {}, next);
      if (sub && Object.keys(sub).length) result[key] = sub;
    } else if (String(base ?? "") !== String(next ?? "")) {
      result[key] = next;
    }
  }
  return result;
}

const TextField = ({ label, value, defaultValue, onChange, multiline, testid }) => {
  const isOverridden = String(value ?? "") !== String(defaultValue ?? "");
  return (
    <div className="space-y-1.5">
      <div className="flex items-center justify-between gap-2">
        <label className="text-xs font-medium text-[#1B2349]">{label}</label>
        <div className="flex items-center gap-2">
          {isOverridden && (
            <span className="text-[10px] uppercase tracking-wide font-semibold text-[#3090A8] bg-[#3090A8]/10 px-1.5 py-0.5 rounded">
              edited
            </span>
          )}
          <button
            type="button"
            onClick={() => onChange(defaultValue ?? "")}
            disabled={!isOverridden}
            className="text-[11px] text-[#718096] hover:text-[#3090A8] disabled:opacity-30 disabled:hover:text-[#718096] flex items-center gap-1"
            data-testid={`${testid}-reset`}
          >
            <RotateCcw className="w-3 h-3" /> Reset
          </button>
        </div>
      </div>
      {multiline ? (
        <Textarea
          value={value ?? ""}
          onChange={(e) => onChange(e.target.value)}
          className="rounded-lg text-sm font-mono min-h-[110px]"
          data-testid={testid}
        />
      ) : (
        <Input
          value={value ?? ""}
          onChange={(e) => onChange(e.target.value)}
          className="rounded-lg text-sm"
          data-testid={testid}
        />
      )}
    </div>
  );
};

const ThemeFieldGroup = ({ groupKey, defaults, edited, onChange }) => {
  const label = THEME_FIELD_LABELS[groupKey]?._label || groupKey;
  const subFields = Object.keys(THEME_FIELD_LABELS[groupKey] || {}).filter((k) => k !== "_label");
  if (!subFields.length) return null;

  return (
    <details
      className="border border-slate-200 rounded-xl bg-white shadow-[0_2px_8px_rgba(0,0,0,0.03)]"
      data-testid={`theme-group-${groupKey}`}
    >
      <summary className="cursor-pointer px-4 py-3 flex items-center justify-between text-sm font-medium text-[#1B2349]">
        <span className="flex items-center gap-2">
          <Sparkles className="w-4 h-4 text-[#3090A8]" />
          {label}
        </span>
        <ChevronDown className="w-4 h-4 text-[#718096]" />
      </summary>
      <div className="px-4 pb-4 space-y-4">
        {subFields.map((subKey) => {
          const fullLabel = THEME_FIELD_LABELS[groupKey][subKey];
          const baseVal = defaults?.[subKey] ?? "";
          const editedVal = edited?.[subKey] ?? baseVal;
          return (
            <TextField
              key={subKey}
              label={fullLabel}
              value={editedVal}
              defaultValue={baseVal}
              multiline={isLongField(baseVal) || isLongField(editedVal)}
              onChange={(v) => onChange(subKey, v)}
              testid={`theme-${groupKey}-${subKey}`}
            />
          );
        })}
      </div>
    </details>
  );
};

const INTERVENTION_TITLES = {
  quiet_the_worry: "Quiet The Worry",
  overcome_overwhelm: "Overcome Your Overwhelm",
  dial_down_tension: "Dial Down Body Tension",
  healing_old_stories: "Healing Old Stories To Let Them Go",
  beat_stage_fright: "Beat Stage Fright",
  fear_of_sleeplessness: "Fear Of Sleeplessness",
  shake_off_sadness: "Shake Off The Blues",
  boost_self_worth: "Boost Your Self-Worth",
  hold_boundaries: "Hold Your Boundaries",
  relief_from_grief: "Relief From Grief",
  calm_your_anger: "Calm Your Anger",
  enhance_performance: "Enhance Your Performance",
  new_habit_new_you: "New Habit, New You",
  headache_relief: "Headache Relief",
  nightmare_reset: "Nightmare Reset",
};

const buildInitialState = (type) => {
  const defaultTasks = INTERVENTION_TASKS[type] || [];
  const defaultTheme = THEMES[type] || {};
  // Editable mirrors of defaults (we'll patch them with overrides on load).
  const editedTasks = defaultTasks.map((t) => ({
    title: t.title ?? "",
    content: t.content ?? "",
    inputPrompt: t.inputPrompt ?? "",
  }));
  const editedTheme = JSON.parse(JSON.stringify(defaultTheme));
  return { defaultTasks, defaultTheme, editedTasks, editedTheme };
};

export const ContentEditorPanel = () => {
  const types = useMemo(() => Object.keys(INTERVENTION_TASKS), []);
  const [selectedType, setSelectedType] = useState(types[0]);
  const [state, setState] = useState(() => buildInitialState(types[0]));
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [updatedMeta, setUpdatedMeta] = useState(null);

  // Load the override for the currently selected practice.
  useEffect(() => {
    let active = true;
    setLoading(true);
    const init = buildInitialState(selectedType);
    setState(init);
    setUpdatedMeta(null);

    axios
      .get(`${API}/admin/content/${selectedType}`, { withCredentials: true })
      .then((res) => {
        if (!active) return;
        const override = res.data || {};
        const nextTasks = init.editedTasks.map((t, idx) => {
          const patch = (override.tasks || []).find((x) => x.index === idx);
          return patch ? { ...t, ...patch } : t;
        });
        const nextTheme = mergeTheme(init.editedTheme, override.theme || {});
        setState({ ...init, editedTasks: nextTasks, editedTheme: nextTheme });
        if (override.updated_at) {
          setUpdatedMeta({
            at: override.updated_at,
            by: override.updated_by_email,
          });
        }
      })
      .catch((err) => {
        logger.warn("content fetch failed", err?.message || err);
      })
      .finally(() => active && setLoading(false));

    return () => { active = false; };
  }, [selectedType]);

  const hasChanges = useMemo(() => {
    if (!state) return false;
    const taskPatches = diffTasks(state.defaultTasks, state.editedTasks);
    const themePatch = diffTheme(state.defaultTheme, state.editedTheme);
    return taskPatches.length > 0 || Object.keys(themePatch).length > 0;
  }, [state]);

  const updateTaskField = (idx, key, value) => {
    setState((s) => {
      const tasks = s.editedTasks.slice();
      tasks[idx] = { ...tasks[idx], [key]: value };
      return { ...s, editedTasks: tasks };
    });
  };

  const updateThemeField = (groupKey, subKey, value) => {
    setState((s) => {
      const nextGroup = { ...(s.editedTheme[groupKey] || {}), [subKey]: value };
      return { ...s, editedTheme: { ...s.editedTheme, [groupKey]: nextGroup } };
    });
  };

  const updateThemeTopLevel = (key, value) => {
    setState((s) => ({ ...s, editedTheme: { ...s.editedTheme, [key]: value } }));
  };

  const resetAll = async () => {
    if (!window.confirm("Reset all admin edits for this practice back to defaults? This goes live immediately for everyone.")) return;
    try {
      await axios.delete(`${API}/admin/content/${selectedType}`, { withCredentials: true });
    } catch (err) {
      if (err?.response?.status !== 404) {
        logger.error("reset failed", err);
        toast.error("Could not reset — please try again.");
        return;
      }
    }
    setContentOverrideFor(selectedType, null);
    setState(buildInitialState(selectedType));
    setUpdatedMeta(null);
    toast.success("Reset to defaults — live for all users.");
  };

  const onSaveClick = () => {
    if (!hasChanges) {
      toast("No changes to publish.");
      return;
    }
    setConfirmOpen(true);
  };

  const doPublish = async () => {
    setSaving(true);
    const taskPatches = diffTasks(state.defaultTasks, state.editedTasks);
    const themePatch = diffTheme(state.defaultTheme, state.editedTheme);
    try {
      const res = await axios.put(
        `${API}/admin/content/${selectedType}`,
        { tasks: taskPatches, theme: themePatch },
        { withCredentials: true },
      );
      setContentOverrideFor(selectedType, {
        tasks: res.data.tasks || [],
        theme: res.data.theme || {},
      });
      if (res.data.updated_at) {
        setUpdatedMeta({ at: res.data.updated_at, by: res.data.updated_by_email });
      } else {
        setUpdatedMeta(null);
      }
      // Refresh global cache so other admin tabs see the new state.
      loadContentOverrides({ force: true });
      toast.success("Published — live for all users.");
      setConfirmOpen(false);
    } catch (err) {
      logger.error("save failed", err);
      toast.error("Could not publish — please try again.");
    } finally {
      setSaving(false);
    }
  };

  if (!state) return null;

  return (
    <div className="space-y-6" data-testid="content-editor-panel">
      <div className="bg-white rounded-2xl p-5 border border-slate-100 shadow-[0_2px_8px_rgba(0,0,0,0.03)]">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <div>
            <h2 className="font-outfit font-semibold text-lg text-[#1B2349]">Practice content editor</h2>
            <p className="text-sm text-[#718096]">
              Edit task copy. Saving publishes immediately for all users. Reset buttons restore the bundled defaults.
            </p>
          </div>
          <div className="flex items-center gap-2">
            <select
              value={selectedType}
              onChange={(e) => setSelectedType(e.target.value)}
              className="rounded-xl border border-slate-200 px-3 py-2 text-sm bg-white text-[#1B2349]"
              data-testid="content-editor-select"
            >
              {types.map((t) => (
                <option key={t} value={t}>{INTERVENTION_TITLES[t] || t}</option>
              ))}
            </select>
          </div>
        </div>
        {updatedMeta?.at && (
          <p className="text-xs text-[#718096] mt-3">
            Last edited {new Date(updatedMeta.at).toLocaleString()} by {updatedMeta.by || "—"}
          </p>
        )}
      </div>

      {loading ? (
        <div className="bg-white rounded-2xl p-10 border border-slate-100 text-center text-[#718096]" data-testid="content-editor-loading">
          Loading content…
        </div>
      ) : (
        <>
          {/* Theme noun (top-level) */}
          {state.defaultTheme?.theme !== undefined && (
            <div className="bg-white rounded-2xl p-5 border border-slate-100 shadow-[0_2px_8px_rgba(0,0,0,0.03)]">
              <TextField
                label={THEME_FIELD_LABELS.theme}
                value={state.editedTheme.theme}
                defaultValue={state.defaultTheme.theme}
                onChange={(v) => updateThemeTopLevel("theme", v)}
                testid="theme-noun"
              />
            </div>
          )}

          {/* Theme groups (t3..t8) — these control rendered copy for tasks 3-8 */}
          <div className="space-y-3">
            <h3 className="font-outfit font-medium text-sm text-[#1B2349] flex items-center gap-2">
              <Sparkles className="w-4 h-4 text-[#3090A8]" />
              Tasks 3 – 8 (template-driven)
            </h3>
            {["t3", "t4", "t5", "t6", "t7", "t8"].map((g) => (
              <ThemeFieldGroup
                key={g}
                groupKey={g}
                defaults={state.defaultTheme?.[g]}
                edited={state.editedTheme?.[g]}
                onChange={(subKey, value) => updateThemeField(g, subKey, value)}
              />
            ))}
          </div>

          {/* Raw task editors (1..9) */}
          <div className="space-y-3">
            <h3 className="font-outfit font-medium text-sm text-[#1B2349] flex items-center gap-2">
              <FileText className="w-4 h-4 text-[#3090A8]" />
              Tasks 1 – 9 (raw title + body)
            </h3>
            {state.editedTasks.map((task, idx) => {
              const def = state.defaultTasks[idx] || {};
              return (
                <details
                  key={idx}
                  className="border border-slate-200 rounded-xl bg-white shadow-[0_2px_8px_rgba(0,0,0,0.03)]"
                  data-testid={`task-editor-${idx + 1}`}
                >
                  <summary className="cursor-pointer px-4 py-3 flex items-center justify-between text-sm font-medium text-[#1B2349]">
                    <span>Task {idx + 1} — {def.title || "(untitled)"}</span>
                    <ChevronDown className="w-4 h-4 text-[#718096]" />
                  </summary>
                  <div className="px-4 pb-4 space-y-4">
                    <TextField
                      label="Title"
                      value={task.title}
                      defaultValue={def.title}
                      onChange={(v) => updateTaskField(idx, "title", v)}
                      testid={`task-${idx + 1}-title`}
                    />
                    <TextField
                      label="Body"
                      value={task.content}
                      defaultValue={def.content}
                      multiline
                      onChange={(v) => updateTaskField(idx, "content", v)}
                      testid={`task-${idx + 1}-content`}
                    />
                    {(def.inputPrompt || task.inputPrompt) && (
                      <TextField
                        label="Input prompt"
                        value={task.inputPrompt}
                        defaultValue={def.inputPrompt || ""}
                        onChange={(v) => updateTaskField(idx, "inputPrompt", v)}
                        testid={`task-${idx + 1}-input`}
                      />
                    )}
                  </div>
                </details>
              );
            })}
          </div>

          {/* Bottom action bar */}
          <div className="sticky bottom-4 z-10 bg-white rounded-2xl p-4 border border-slate-100 shadow-[0_4px_20px_rgba(0,0,0,0.08)] flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
            <p className="text-xs text-[#718096]">
              {hasChanges ? (
                <span className="text-[#3090A8] font-medium">You have unpublished changes.</span>
              ) : (
                <span>No changes since last publish.</span>
              )}
            </p>
            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                onClick={resetAll}
                className="rounded-xl"
                data-testid="content-reset-all-btn"
              >
                <RotateCcw className="w-4 h-4 mr-2" /> Reset to defaults
              </Button>
              <Button
                onClick={onSaveClick}
                disabled={!hasChanges || saving}
                className="btn-primary rounded-xl"
                data-testid="content-publish-btn"
              >
                <Save className="w-4 h-4 mr-2" /> {saving ? "Publishing…" : "Publish"}
              </Button>
            </div>
          </div>
        </>
      )}

      <Dialog open={confirmOpen} onOpenChange={setConfirmOpen}>
        <DialogContent data-testid="content-publish-confirm">
          <DialogTitle className="flex items-center gap-2 text-[#1B2349]">
            <AlertTriangle className="w-5 h-5 text-[#E2B887]" />
            Publish changes live?
          </DialogTitle>
          <DialogDescription>
            Saving will immediately update the copy for every user on{" "}
            <strong>{INTERVENTION_TITLES[selectedType] || selectedType}</strong>.
            New TTS narration audio will regenerate automatically the first time
            a user opens an edited task (a brief delay on that task only).
          </DialogDescription>
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setConfirmOpen(false)} data-testid="content-publish-cancel">
              Cancel
            </Button>
            <Button onClick={doPublish} disabled={saving} className="btn-primary" data-testid="content-publish-confirm-btn">
              {saving ? "Publishing…" : "Publish now"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

// Deep-merge helper used to layer override theme atop defaults.
function mergeTheme(base, override) {
  if (override == null) return base;
  if (typeof override !== "object" || Array.isArray(override)) return override;
  const result = JSON.parse(JSON.stringify(base || {}));
  for (const key of Object.keys(override)) {
    const next = override[key];
    if (next && typeof next === "object" && !Array.isArray(next)) {
      result[key] = mergeTheme(result[key], next);
    } else {
      result[key] = next;
    }
  }
  return result;
}
